import React, { useEffect, useRef, useState } from 'react';
import { PitchDetector } from '../utils/PitchDetector';
import { normalizeNoteForDetection } from '../utils/noteUtils';
import AudioLevelMeter from './AudioLevelMeter';
import './AudioManager.css';

interface AudioManagerProps {
  isGameActive: boolean;
  targetNote: string;
  onNoteDetected: (detectedNote: string, isCorrect: boolean, responseTime: number) => void;
  gameState: 'playing' | 'success' | 'transition';
  sensitivity: number;
  onSensitivityChange: (sensitivity: number) => void;
}

const AudioManager: React.FC<AudioManagerProps> = ({
  isGameActive,
  targetNote,
  onNoteDetected,
  gameState,
  sensitivity,
  onSensitivityChange
}) => {
  const [isListening, setIsListening] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [detectedNote, setDetectedNote] = useState<string>('');
  const startTimeRef = useRef<number>(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [gainNode, setGainNode] = useState<GainNode | null>(null);
  const [detectedFrequency, setDetectedFrequency] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');
  const [sustainedNote, setSustainedNote] = useState('');

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const pitchDetectorRef = useRef<PitchDetector | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const currentSustainedNoteRef = useRef<string>('');
  const noteFirstDetectedRef = useRef<number>(0);
  const noteLastSeenRef = useRef<number>(0);
  const processedNoteRef = useRef<string>('');
  const targetNoteRef = useRef<string>(targetNote);
  const isGeneratingNewNoteRef = useRef<boolean>(false);
  const wrongNoteStartTimeRef = useRef<number>(0);
  const wrongNoteRef = useRef<string>('');

  // Keep targetNoteRef in sync with the prop
  useEffect(() => {
    targetNoteRef.current = targetNote;
  }, [targetNote]);

  useEffect(() => {
    if (isGameActive && targetNote) {
      const newStartTime = Date.now();
      startTimeRef.current = newStartTime;
    }
  }, [isGameActive, targetNote]);

  useEffect(() => {
    const startCapture = async () => {
      if (isGameActive) {
        await startAudioCapture();
      } else {
        stopAudioCapture();
      }
    };

    startCapture();

    return () => stopAudioCapture();
  }, [isGameActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAudioCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      });

      streamRef.current = stream;
      setHasPermission(true);

      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 44100
      });

      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();

      // Create gain node for automatic gain control
      const gain = audioContextRef.current.createGain();
      gain.gain.setValueAtTime(2.0, audioContextRef.current.currentTime); // Start with 2x gain for acoustic guitars
      setGainNode(gain);

      analyserRef.current.fftSize = 8192; // Increased for better bass frequency resolution
      analyserRef.current.smoothingTimeConstant = 0.8;

      // Connect: source -> gain -> analyser
      source.connect(gain);
      gain.connect(analyserRef.current);

      pitchDetectorRef.current = new PitchDetector(audioContextRef.current.sampleRate);

      setIsListening(true);
      startPitchDetection();

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setHasPermission(false);
    }
  };

  const stopAudioCapture = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    pitchDetectorRef.current = null;
    setGainNode(null);
    setIsListening(false);
    setDetectedNote('');
    setAudioLevel(0);
    setDetectedFrequency(0);
    setDebugInfo('');
    setSustainedNote('');
    currentSustainedNoteRef.current = '';
    noteFirstDetectedRef.current = 0;
    noteLastSeenRef.current = 0;
    processedNoteRef.current = '';
    isGeneratingNewNoteRef.current = false;
    startTimeRef.current = 0;
    wrongNoteStartTimeRef.current = 0;
    wrongNoteRef.current = '';
  };

  const startPitchDetection = () => {
    if (!analyserRef.current || !pitchDetectorRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Float32Array(bufferLength);

    const detectPitch = () => {
      if (!isGameActive || !analyserRef.current || !pitchDetectorRef.current) return;

      analyserRef.current.getFloatTimeDomainData(dataArray);

      // Calculate and update audio level for the meter
      const rms = pitchDetectorRef.current.calculateRMS(dataArray);
      const levelPercentage = Math.min(100, (rms * 1000)); // Scale for display
      setAudioLevel(levelPercentage);

      // Automatic gain control: adjust gain based on signal level and sensitivity
      if (gainNode && audioContextRef.current) {
        let targetGain = 2.0 * sensitivity; // Apply sensitivity to base gain

        const sensitivityAdjustedRms = rms / sensitivity; // Adjust RMS thresholds based on sensitivity

        if (sensitivityAdjustedRms > 0.05) targetGain = 1.0 * sensitivity;      // Strong signal, reduce gain
        else if (sensitivityAdjustedRms > 0.02) targetGain = 1.5 * sensitivity;  // Medium signal
        else if (sensitivityAdjustedRms < 0.005) targetGain = 3.0 * sensitivity; // Very quiet signal, boost more

        // Smoothly adjust gain to avoid clicks
        gainNode.gain.exponentialRampToValueAtTime(
          targetGain,
          audioContextRef.current.currentTime + 0.1
        );
      }

      const frequency = pitchDetectorRef.current.detectPitch(dataArray, sensitivity);
      setDetectedFrequency(frequency);

      // Debug information
      const confidence = pitchDetectorRef.current.getLastConfidence();
      const debugString = `RMS: ${rms.toFixed(4)}, Freq: ${frequency.toFixed(1)}Hz, Conf: ${confidence.toFixed(2)}, Target: ${targetNote}, Sustained: ${sustainedNote}`;
      setDebugInfo(debugString);

      // Note sustain logic using refs for immediate updates
      const currentTime = Date.now();
      const note = frequency > 0 ? pitchDetectorRef.current.frequencyToNote(frequency) : null;

      // Use refs for immediate access to current values
      const currentSustainedNote = currentSustainedNoteRef.current;
      let finalDisplayedNote = currentSustainedNote;

      if (note && gameState === 'playing') {
        // Any valid note detection updates the last seen time (only when not transitioning)
        noteLastSeenRef.current = currentTime;

        if (!currentSustainedNote) {
          // No sustained note yet, establish this one immediately
          currentSustainedNoteRef.current = note;
          noteFirstDetectedRef.current = currentTime;
          processedNoteRef.current = ''; // Reset processed note for new sustained note
          setSustainedNote(note);
          finalDisplayedNote = note;
        } else if (note === currentSustainedNote) {
          // Same note as sustained, just keep showing it
          finalDisplayedNote = note;
        } else {
          // Different note detected - use adaptive stability threshold
          // Shorter threshold for bass frequencies to handle vibrato better
          const isBassFrequency = frequency < 200;
          const stabilityThreshold = isBassFrequency ? 150 : 300; // 150ms for bass, 300ms for higher frequencies
          
          if ((currentTime - noteFirstDetectedRef.current) > stabilityThreshold) {
            // Different note, and sustained note has been stable long enough, allow change
            currentSustainedNoteRef.current = note;
            noteFirstDetectedRef.current = currentTime;
            processedNoteRef.current = ''; // Reset processed note for new sustained note
            setSustainedNote(note);
            finalDisplayedNote = note;
            
            // Clear frequency history when switching to a new note
            if (pitchDetectorRef.current) {
              pitchDetectorRef.current.clearFrequencyHistory();
            }
          }
        }
        // If different note but sustained note is still new, ignore the change
      } else {
        // No note detected this frame
        if (currentSustainedNote) {
          if ((currentTime - noteLastSeenRef.current) > 2000) {
            // Clear sustained note if not seen for 2 full seconds
            currentSustainedNoteRef.current = '';
            noteFirstDetectedRef.current = 0;
            processedNoteRef.current = '';
            setSustainedNote('');
            finalDisplayedNote = '';
          } else {
            // Keep showing the sustained note
            finalDisplayedNote = currentSustainedNote;
          }
        }
      }

      setDetectedNote(finalDisplayedNote);

      // Game logic - handle correct and wrong notes with different timing
      const currentProcessedNote = processedNoteRef.current;
      const currentSustainedNoteForGame = currentSustainedNoteRef.current;
      const currentTargetNote = targetNoteRef.current;

      if (currentSustainedNoteForGame && currentTargetNote && currentSustainedNoteForGame !== currentProcessedNote && gameState === 'playing') {
        // Normalize both notes for comparison (flats vs sharps)
        const detectedNoteNormalized = normalizeNoteForDetection(currentSustainedNoteForGame);
        const targetNoteNormalized = normalizeNoteForDetection(currentTargetNote);
        const isCorrect = detectedNoteNormalized === targetNoteNormalized;
        
        if (isCorrect) {
          // Process correct notes immediately for responsive positive feedback
          processedNoteRef.current = currentSustainedNoteForGame;
          const responseTime = (Date.now() - startTimeRef.current) / 1000;
          onNoteDetected(currentSustainedNoteForGame, true, responseTime);
          
          // Clear wrong note tracking when correct note is played
          wrongNoteStartTimeRef.current = 0;
          wrongNoteRef.current = '';
        } else {
          // For wrong notes, require longer sustained time before triggering error
          if (wrongNoteRef.current !== currentSustainedNoteForGame) {
            // New wrong note detected, start timing it
            wrongNoteRef.current = currentSustainedNoteForGame;
            wrongNoteStartTimeRef.current = currentTime;
          } else if (wrongNoteStartTimeRef.current > 0) {
            // Same wrong note sustained, check if it's been long enough
            const wrongNoteDuration = currentTime - wrongNoteStartTimeRef.current;
            const wrongNoteThreshold = 500; // Require 500ms of sustained wrong note before error feedback
            
            if (wrongNoteDuration >= wrongNoteThreshold) {
              // Wrong note has been sustained long enough, trigger error feedback
              processedNoteRef.current = currentSustainedNoteForGame;
              const responseTime = (Date.now() - startTimeRef.current) / 1000;
              onNoteDetected(currentSustainedNoteForGame, false, responseTime);
              
              // Reset wrong note tracking after processing
              wrongNoteStartTimeRef.current = 0;
              wrongNoteRef.current = '';
            }
          }
        }
      } else if (!currentSustainedNoteForGame) {
        // No note detected, reset wrong note tracking
        wrongNoteStartTimeRef.current = 0;
        wrongNoteRef.current = '';
      }

      animationFrameRef.current = requestAnimationFrame(detectPitch);
    };

    detectPitch();
  };

  return (
    <div className="audio-manager">
      <AudioLevelMeter
        level={audioLevel}
        isActive={isListening && hasPermission}
        sensitivity={sensitivity}
        onSensitivityChange={onSensitivityChange}
      />

      <div className="audio-status">
        {!hasPermission && isGameActive && (
          <div className="permission-request">
            <p>🎤 Microphone access required</p>
            <p className="permission-help">
              Click "Practice Single Notes" and allow microphone access when prompted
            </p>
          </div>
        )}

        {hasPermission && (
          <div className="audio-info">
            <div className="listening-indicator">
              {isListening ? (
                <span className="listening-active">🎤 Listening...</span>
              ) : (
                <span className="listening-inactive">🎤 Not listening</span>
              )}
            </div>

            <div className="detection-info">
              {detectedFrequency > 0 && (
                <div className="frequency-display">
                  <span className="freq-label">Frequency:</span>
                  <span className="freq-value">{detectedFrequency.toFixed(1)} Hz</span>
                </div>
              )}

              {detectedNote && (
                <div className="detected-note">
                  Detected: <strong>{detectedNote}</strong>
                </div>
              )}

              {!detectedNote && detectedFrequency > 0 && (
                <div className="no-note-detected">
                  <span className="freq-value">{detectedFrequency.toFixed(1)} Hz</span>
                  <span className="no-note-reason">
                    {detectedFrequency < 80 ? '(Too low)' :
                     detectedFrequency > 2000 ? '(Too high)' :
                     '(Not a clear note)'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {isListening && debugInfo && (
          <div className="debug-info">
            <small>{debugInfo}</small>
          </div>
        )}

        {hasPermission && isListening && (
          <div className="usage-tips">
            <h4>Tips for better detection:</h4>
            <ul>
              <li>Play single notes clearly (avoid chords)</li>
              <li>Position microphone 6-12 inches from your guitar</li>
              <li>Strum with moderate force</li>
              <li>Ensure good room acoustics (avoid echo)</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioManager;
